import React, {useState, useEffect, useContext} from 'react'
import {motion, AnimatePresence} from 'framer-motion'
import {useNavigate, useLocation} from 'react-router-dom'
import {TavilyService} from '../../services/tavilyService'
import {SupabaseService} from '../../services/supabaseService'
import {CreditService} from '../../services/creditService'
import KeywordInput from '../Search/KeywordInput'
import ProgressBar from '../Search/ProgressBar'
import ResultsDisplay from '../Results/ResultsDisplay'
import SearchHistory from './SearchHistory'
import AnalyticsDashboard from '../Analytics/AnalyticsDashboard'
import CreditBalance from '../Credits/CreditBalance'
import CreditHistory from '../Credits/CreditHistory'
import CreditPurchase from '../Credits/CreditPurchase'
import LinkDirectory from '../LinkDirectory/LinkDirectory'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import {ThemeContext} from '../../App'
import {supabase} from '../../config/supabase'

const {FiSearch, FiBarChart, FiCreditCard, FiLogOut, FiUser, FiPlus, FiClock, FiDatabase, FiMenu, FiX, FiSun, FiMoon, FiSettings, FiSidebar} = FiIcons

const Dashboard = ({user, onLogout}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const {theme, toggleTheme} = useContext(ThemeContext)
  const [activeTab, setActiveTab] = useState('search')
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [results, setResults] = useState([])
  const [searchHistory, setSearchHistory] = useState([])
  const [creditHistory, setCreditHistory] = useState([])
  const [currentKeyword, setCurrentKeyword] = useState('')
  const [currentSearch, setCurrentSearch] = useState(null)
  const [userCredits, setUserCredits] = useState(0)
  const [showPurchase, setShowPurchase] = useState(false)
  const [error, setError] = useState(null)
  const [isLoadingCredits, setIsLoadingCredits] = useState(true)
  const [creditHistoryPage, setCreditHistoryPage] = useState(1)
  const [creditHistoryTotalPages, setCreditHistoryTotalPages] = useState(1)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [isCustomSearch, setIsCustomSearch] = useState(false)
  const [isSidebarOpen, setIsSidebarOpen] = useState(true)
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false)

  // Handle payment success redirects (HashRouter)
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('payment') === 'success') {
      setActiveTab('credits')
      setShowPaymentSuccess(true)
      // Cleanup query string in hash-based routing
      navigate('/dashboard', { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Auto-dismiss success banner after ~5 seconds
  useEffect(() => {
    if (!showPaymentSuccess) return
    const timerId = setTimeout(() => setShowPaymentSuccess(false), 5000)
    return () => clearTimeout(timerId)
  }, [showPaymentSuccess])

  useEffect(() => {
    if (user) {
      loadSearchHistory()
      loadUserCredits()
    }
  }, [user])

  // Listen for custom search events
  useEffect(() => {
    const handleStartSearch = (event) => {
      const searchKeyword = event.detail.keyword;
      if (searchKeyword) {
        console.log("Starting search for:", searchKeyword);
        handleSearch(searchKeyword);
      }
    };
    
    document.addEventListener('startSearch', handleStartSearch);
    
    return () => {
      document.removeEventListener('startSearch', handleStartSearch);
    };
  }, [userCredits]); // Added userCredits as a dependency to ensure we have the latest credit value

  const loadUserCredits = async () => {
    setIsLoadingCredits(true)
    try {
      const credits = await CreditService.getUserCredits(user.id)
      setUserCredits(credits)
      // Load credit history when credits are loaded
      await loadCreditHistory(1)
      console.log("Credit transactions loaded")
    } catch (error) {
      console.error('Error loading user credits:', error)
    } finally {
      setIsLoadingCredits(false)
    }
  }

  const loadCreditHistory = async (page = 1) => {
    try {
      const [transactions, totalCount] = await Promise.all([
        CreditService.getCreditTransactions(user.id, page, 15),
        CreditService.getCreditTransactionsCount(user.id)
      ])
      setCreditHistory(transactions)
      setCreditHistoryPage(page)
      setCreditHistoryTotalPages(Math.ceil(totalCount / 15))
    } catch (error) {
      console.error('Error loading credit history:', error)
    }
  }

  const handleCreditHistoryPageChange = (page) => {
    if (page >= 1 && page <= creditHistoryTotalPages) {
      loadCreditHistory(page)
    }
  }

  const loadSearchHistory = async () => {
    try {
      const searches = await SupabaseService.getUserSearches(user.id)
      // Do not compute status_counts here; lazy-load in SearchHistory for visible items
      setSearchHistory(searches)
    } catch (error) {
      console.error('Error loading search history:', error)
      setError('Failed to load search history')
    }
  }

  const handleSearch = async (keyword, mode = 'broad') => {
    // Determine required credits based on mode
    const requiredCredits = mode === 'custom' ? 0.1 : 0.5
    // Check if user has sufficient credits
    if (userCredits < requiredCredits) {
      setError(`You need at least ${requiredCredits} credits to perform a ${mode === 'custom' ? 'custom' : 'broad'} search. Please purchase credits to continue.`)
      return
    }

    setLoading(true)
    setProgress(0)
    setResults([])
    setCurrentKeyword(keyword)
    setCurrentSearch(null)
    setError(null)
    setMobileMenuOpen(false)
    setActiveTab('search')
    setIsCustomSearch(mode === 'custom')

    try {
      // Spend credits based on search mode
      const creditTransaction = mode === 'custom'
        ? await CreditService.spendCustomSearchCredit(user.id)
        : await CreditService.spendCredit(user.id)
      if (!creditTransaction) {
        setError('Unable to spend credit. Please try again.')
        setLoading(false)
        return
      }

      // Update user credits immediately
      setUserCredits(prev => Number((prev - requiredCredits).toFixed(4)))

      // Perform the search
      const searchResults = mode === 'custom'
        ? await TavilyService.searchCustomFootprint(keyword, setProgress)
        : await TavilyService.searchBacklinks(keyword, setProgress)

      // Save the search to database
      const savedSearch = await SupabaseService.saveSearch(user.id, keyword, searchResults)

      // Update the credit transaction with search ID
      await CreditService.updateCreditTransaction(user.id, savedSearch.id)

      setResults(searchResults)
      setCurrentSearch(savedSearch)

      // Refresh search history and credit history
      await loadSearchHistory()
      await loadUserCredits()
    } catch (error) {
      console.error('Search error:', error)
      setError('Search failed. Please try again.')
    } finally {
      setLoading(false)
      setProgress(0)
    }
  }

  const handleLoadSearch = async (search) => {
    // Handle directory view separately with filters
    if (search.isDirectory) {
      setActiveTab('directory')
      setCurrentSearch(search) // Store the filters in currentSearch
      setMobileMenuOpen(false)
      return
    }

    let fullSearch = search

    // Fetch full search data if missing results
    if (!search.results || search.results.length === 0) {
      const {data, error} = await supabase
        .from('searches')
        .select('*')
        .eq('id', search.id)
        .single()

      if (error) {
        console.error('Error loading search:', error)
        return
      }
      fullSearch = data
    }

    setCurrentSearch(fullSearch)
    setCurrentKeyword(fullSearch.keyword)
    setResults(fullSearch.results || [])
    setActiveTab('search')
    setMobileMenuOpen(false)
  }

  const handlePurchaseClick = () => {
    setShowPurchase(true)
    setMobileMenuOpen(false)
  }

  const handlePurchaseComplete = async (purchasedCredits) => {
    setUserCredits(prev => prev + purchasedCredits)
    setShowPurchase(false)
    setError(null)

    // Refresh credit history
    await loadUserCredits()
  }

  const handleBackFromPurchase = () => {
    setShowPurchase(false)
  }

  const handleStatusesUpdated = async () => {
    // Refresh search history to update status counts
    await loadSearchHistory()
  }

  const handleContentGenerated = async () => {
    // Refresh user credits after content generation
    await loadUserCredits()
  }

  const handleSearchDeleted = async (searchId) => {
    // Remove the deleted search from the local state
    setSearchHistory(prevHistory => prevHistory.filter(search => search.id !== searchId))

    // If the current search is the one that was deleted, clear it
    if (currentSearch && currentSearch.id === searchId) {
      setCurrentSearch(null)
      setCurrentKeyword('')
      setResults([])
    }
  }

  const handleLogout = async () => {
    if (onLogout) {
      await onLogout()
      navigate('/login')
    }
  }

  const handleBackFromDirectory = () => {
    setActiveTab('search')
    setMobileMenuOpen(false)
  }

  const handleTabChange = (tab) => {
    setActiveTab(tab)
    setMobileMenuOpen(false)
  }

  const handleGoToSettings = () => {
    navigate('/settings');
  };

  useEffect(() => {
    // When switching to the credits tab, ensure credit history is loaded
    if (activeTab === 'credits') {
      loadUserCredits()
    }
  }, [activeTab])

  useEffect(() => {
    // Ensure view starts at the top when switching tabs
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [activeTab])

  if (showPurchase) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
        <CreditPurchase
          user={user}
          onPurchaseComplete={handlePurchaseComplete}
          onBack={handleBackFromPurchase}
        />
      </div>
    )
  }

  if (activeTab === 'directory') {
    // Pass initial filters if available in currentSearch
    const initialFilters = currentSearch && currentSearch.isDirectory
      ? {
          niche: currentSearch.niche || '',
          type: currentSearch.linkType || '',
          search: '',
          favorites: false
        }
      : undefined;

    return (
      <LinkDirectory
        user={user}
        userCredits={userCredits}
        onPurchaseClick={handlePurchaseClick}
        onBack={handleBackFromDirectory}
        initialFilters={initialFilters}
      />
    )
  }

  return (
    <div className={`min-h-screen bg-gray-50 dark:bg-gray-900 dark:text-gray-100 gradient-bg ${isSidebarOpen ? 'lg:pl-64' : 'lg:pl-20'}`}>
      {/* Desktop Sidebar (LG+ only) */}
      <aside className={`hidden lg:flex fixed top-0 left-0 h-screen bg-white dark:bg-gray-800 border-r dark:border-gray-700 shadow-sm flex-col ${isSidebarOpen ? 'w-64' : 'w-20'}`}>
        <div className="flex items-center justify-between px-3 py-4">
          {isSidebarOpen && (
            <img src="https://k9links.com/wp-content/uploads/2025/08/K9-Links-Logo.webp" alt="K9 Links" className="h-12 w-auto mx-auto" />
          )}
          {isSidebarOpen && (
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white no-border"
              title="Collapse sidebar"
            >
              <SafeIcon icon={FiSidebar} className="w-6 h-6" />
            </button>
          )}
        </div>
        {!isSidebarOpen && (
          <div className="flex flex-col items-center">
            <img src="https://k9links.com/wp-content/uploads/2025/08/K9-Links-Icon-Logo.webp" alt="K9 Links" className="w-12 h-auto mx-auto mb-2" onClick={() => setIsSidebarOpen(true)} />
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="p-2 mb-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white no-border"
              title="Expand sidebar"
            >
              <SafeIcon icon={FiSidebar} className="w-7 h-7 ml-[3px]" />
            </button>
          </div>
        )}

        {/* Credits Panel (open only) */}
        {isSidebarOpen && (
          <div className="px-3 pb-3">
            <CreditBalance
              user={user}
              onPurchaseClick={handlePurchaseClick}
              refreshCredits={loadUserCredits}
            />
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 px-2 space-y-1">
          <button
            onClick={() => setActiveTab('search')}
            className={`w-full relative group flex items-center px-3 py-2 rounded-lg ${
              activeTab === 'search' ? 'bg-[#D59A4C20] text-[#D59A4C]' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <SafeIcon icon={FiSearch} className={`w-7 h-7 ${!isSidebarOpen ? 'ml-[7px]' : ''}`} />
            {isSidebarOpen && <span className="ml-3">Search</span>}
            {!isSidebarOpen && (
              <span className="pointer-events-none absolute left-full ml-2 py-1 px-3 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg whitespace-nowrap text-sm text-gray-700 dark:text-gray-200 opacity-0 group-hover:opacity-100 transform transition-all duration-150 origin-left group-hover:translate-x-1 z-50 border-l-4 border-[#D59A4C] lg:block">
                Search
              </span>
            )}
          </button>

          <button
            onClick={() => handleTabChange('directory')}
            className={`w-full relative group flex items-center px-3 py-2 rounded-lg ${
              activeTab === 'directory' ? 'bg-[#D59A4C20] text-[#D59A4C]' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <SafeIcon icon={FiDatabase} className={`w-7 h-7 ${!isSidebarOpen ? 'ml-[7px]' : ''}`} />
            {isSidebarOpen && <span className="ml-3">Directory</span>}
            {!isSidebarOpen && (
              <span className="pointer-events-none absolute left-full ml-2 py-1 px-3 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg whitespace-nowrap text-sm text-gray-700 dark:text-gray-200 opacity-0 group-hover:opacity-100 transform transition-all duration-150 origin-left group-hover:translate-x-1 z-50 border-l-4 border-[#D59A4C] lg:block">
                Directory
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('analytics')}
            className={`w-full relative group flex items-center px-3 py-2 rounded-lg ${
              activeTab === 'analytics' ? 'bg-[#D59A4C20] text-[#D59A4C]' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <SafeIcon icon={FiBarChart} className={`w-7 h-7 ${!isSidebarOpen ? 'ml-[7px]' : ''}`} />
            {isSidebarOpen && <span className="ml-3">Analytics</span>}
            {!isSidebarOpen && (
              <span className="pointer-events-none absolute left-full ml-2 py-1 px-3 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg whitespace-nowrap text-sm text-gray-700 dark:text-gray-200 opacity-0 group-hover:opacity-100 transform transition-all duration-150 origin-left group-hover:translate-x-1 z-50 border-l-4 border-[#D59A4C] lg:block">
                Analytics
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('history')}
            className={`w-full relative group flex items-center px-3 py-2 rounded-lg ${
              activeTab === 'history' ? 'bg-[#D59A4C20] text-[#D59A4C]' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <SafeIcon icon={FiClock} className={`w-7 h-7 ${!isSidebarOpen ? 'ml-[7px]' : ''}`} />
            {isSidebarOpen && <span className="ml-3">History</span>}
            {!isSidebarOpen && (
              <span className="pointer-events-none absolute left-full ml-2 py-1 px-3 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg whitespace-nowrap text-sm text-gray-700 dark:text-gray-200 opacity-0 group-hover:opacity-100 transform transition-all duration-150 origin-left group-hover:translate-x-1 z-50 border-l-4 border-[#D59A4C] lg:block">
                History
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('credits')}
            className={`w-full relative group flex items-center px-3 py-2 rounded-lg ${
              activeTab === 'credits' ? 'bg-[#D59A4C20] text-[#D59A4C]' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <SafeIcon icon={FiCreditCard} className={`w-7 h-7 ${!isSidebarOpen ? 'ml-[7px]' : ''}`} />
            {isSidebarOpen && <span className="ml-3">Credits</span>}
            {!isSidebarOpen && (
              <span className="pointer-events-none absolute left-full ml-2 py-1 px-3 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg whitespace-nowrap text-sm text-gray-700 dark:text-gray-200 opacity-0 group-hover:opacity-100 transform transition-all duration-150 origin-left group-hover:translate-x-1 z-50 border-l-4 border-[#D59A4C] lg:block">
                Credits
              </span>
            )}
          </button>
        </nav>

        {/* Footer actions */}
        <div className="px-2 py-3 mt-auto border-t dark:border-gray-700">
          <button
            onClick={toggleTheme}
            className="w-full relative group flex items-center px-3 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 no-border"
          >
            <SafeIcon icon={theme === 'dark' ? FiSun : FiMoon} className={`w-7 h-7 ${!isSidebarOpen ? 'ml-[7px]' : ''}`} />
            {isSidebarOpen && <span className="ml-3">Light/Dark</span>}
            {!isSidebarOpen && (
              <span className="pointer-events-none absolute left-full ml-2 py-1 px-3 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg whitespace-nowrap text-sm text-gray-700 dark:text-gray-200 opacity-0 group-hover:opacity-100 transform transition-all duration-150 origin-left group-hover:translate-x-1 z-50 border-l-4 border-[#D59A4C] lg:block">Brightness</span>
            )}
          </button>

          <div
            className="w-full relative group flex items-center px-3 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
            onClick={handleGoToSettings}
          >
            <SafeIcon icon={FiUser} className={`w-6 h-6 ${!isSidebarOpen ? 'ml-[7px]' : ''}`} />
            {isSidebarOpen && <span className="ml-3 truncate">{user?.email}</span>}
            {!isSidebarOpen && (
              <span className="pointer-events-none absolute left-full ml-2 py-1 px-3 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg whitespace-nowrap text-sm text-gray-700 dark:text-gray-200 opacity-0 group-hover:opacity-100 transform transition-all duration-150 origin-left group-hover:translate-x-1 z-50 border-l-4 border-[#D59A4C] lg:block">Settings</span>
            )}
          </div>

          <button
            onClick={handleLogout}
            className="w-full relative group flex items-center px-3 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 no-border"
          >
            <SafeIcon icon={FiLogOut} className={`w-6 h-6 ${!isSidebarOpen ? 'ml-[7px]' : ''}`} />
            {isSidebarOpen && <span className="ml-3">Sign Out</span>}
            {!isSidebarOpen && (
              <span className="pointer-events-none absolute left-full ml-2 py-1 px-3 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg whitespace-nowrap text-sm text-gray-700 dark:text-gray-200 opacity-0 group-hover:opacity-100 transform transition-all duration-150 origin-left group-hover:translate-x-1 z-50 border-l-4 border-[#D59A4C] lg:block">Signout</span>
            )}
          </button>
        </div>
      </aside>

      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700 gradient-bg lg:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img src="https://k9links.com/wp-content/uploads/2025/08/K9-Links-Logo.webp" alt="K9 Links" className="h-12 w-auto mr-3" />
            </div>

            <div className="hidden md:flex items-center space-x-4">
              <CreditBalance
                user={user}
                onPurchaseClick={handlePurchaseClick}
                refreshCredits={loadUserCredits}
              />

              {/* Theme Toggle - Desktop/Tablet */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
              >
                <SafeIcon icon={theme === 'dark' ? FiSun : FiMoon} className="w-5 h-5" />
              </button>

              <div
                className="hidden md:flex items-center text-sm text-gray-600 dark:text-gray-300 cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors"
                onClick={handleGoToSettings}
              >
                <SafeIcon icon={FiUser} className="w-4 h-4 mr-1" />
                {user?.email}
              </div>

              <button
                onClick={handleLogout}
                className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
              >
                <SafeIcon icon={FiLogOut} className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Sign Out</span>
              </button>
            </div>

            {/* Mobile Header Icons */}
            <div className="flex md:hidden items-center space-x-2">
              {/* Theme Toggle - Mobile */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
              >
                <SafeIcon icon={theme === 'dark' ? FiSun : FiMoon} className="w-5 h-5" />
              </button>

              <button
                onClick={handleLogout}
                className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
              >
                <SafeIcon icon={FiLogOut} className="w-5 h-5" />
              </button>

              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
              >
                <SafeIcon icon={mobileMenuOpen ? FiX : FiMenu} className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Mobile Credits Display */}
          <div className="md:hidden py-2">
            <CreditBalance
              user={user}
              onPurchaseClick={handlePurchaseClick}
              refreshCredits={loadUserCredits}
              isMobile={true}
            />
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white dark:bg-gray-800 border-b dark:border-gray-700 shadow-md">
          <div className="px-4 py-2 space-y-2">
            <button
              onClick={() => handleTabChange('search')}
              className={`w-full flex items-center px-4 py-3 rounded-lg ${
                activeTab === 'search' ? 'bg-[#D59A4C20] text-[#D59A4C]' : 'text-gray-700 dark:text-gray-300'
              }`}
            >
              <SafeIcon icon={FiSearch} className="w-5 h-5 mr-3" />
              Search
            </button>

            <button
              onClick={() => handleTabChange('directory')}
              className={`w-full flex items-center px-4 py-3 rounded-lg ${
                activeTab === 'directory' ? 'bg-[#D59A4C20] text-[#D59A4C]' : 'text-gray-700 dark:text-gray-300'
              }`}
            >
              <SafeIcon icon={FiDatabase} className="w-5 h-5 mr-3" />
              Directory
            </button>

            <button
              onClick={() => handleTabChange('analytics')}
              className={`w-full flex items-center px-4 py-3 rounded-lg ${
                activeTab === 'analytics' ? 'bg-[#D59A4C20] text-[#D59A4C]' : 'text-gray-700 dark:text-gray-300'
              }`}
            >
              <SafeIcon icon={FiBarChart} className="w-5 h-5 mr-3" />
              Analytics
            </button>

            <button
              onClick={() => handleTabChange('history')}
              className={`w-full flex items-center px-4 py-3 rounded-lg ${
                activeTab === 'history' ? 'bg-[#D59A4C20] text-[#D59A4C]' : 'text-gray-700 dark:text-gray-300'
              }`}
            >
              <SafeIcon icon={FiClock} className="w-5 h-5 mr-3" />
              History
            </button>

            <button
              onClick={() => handleTabChange('credits')}
              className={`w-full flex items-center px-4 py-3 rounded-lg ${
                activeTab === 'credits' ? 'bg-[#D59A4C20] text-[#D59A4C]' : 'text-gray-700 dark:text-gray-300'
              }`}
            >
              <SafeIcon icon={FiCreditCard} className="w-5 h-5 mr-3" />
              Credits
            </button>

            <button
              onClick={handleGoToSettings}
              className={`w-full flex items-center px-4 py-3 rounded-lg text-gray-700 dark:text-gray-300`}
            >
              <SafeIcon icon={FiSettings} className="w-5 h-5 mr-3" />
              Settings
            </button>

            <div className="pt-2 border-t dark:border-gray-700">
              <div
                className="flex items-center px-4 py-3 text-sm text-gray-600 dark:text-gray-300 cursor-pointer"
                onClick={handleGoToSettings}
              >
                <SafeIcon icon={FiUser} className="w-4 h-4 mr-2" />
                {user?.email}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Navigation */}
      <nav className="hidden md:block lg:hidden bg-white dark:bg-gray-800 border-b dark:border-gray-700 gradient-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('search')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'search'
                  ? 'border-[#D59A4C] text-[#D59A4C]'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <SafeIcon icon={FiSearch} className="w-4 h-4 mr-2 inline" />
              Search
            </button>

            <button
              onClick={() => handleTabChange('directory')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'directory'
                  ? 'border-[#D59A4C] text-[#D59A4C]'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <SafeIcon icon={FiDatabase} className="w-4 h-4 mr-2 inline" />
              Directory
            </button>

            <button
              onClick={() => setActiveTab('analytics')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'analytics'
                  ? 'border-[#D59A4C] text-[#D59A4C]'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <SafeIcon icon={FiBarChart} className="w-4 h-4 mr-2 inline" />
              Analytics
            </button>

            <button
              onClick={() => setActiveTab('history')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'history'
                  ? 'border-[#D59A4C] text-[#D59A4C]'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <SafeIcon icon={FiClock} className="w-4 h-4 mr-2 inline" />
              History
            </button>

            <button
              onClick={() => setActiveTab('credits')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'credits'
                  ? 'border-[#D59A4C] text-[#D59A4C]'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <SafeIcon icon={FiCreditCard} className="w-4 h-4 mr-2 inline" />
              Credits
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 gradient-bg">
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/30 border-l-4 border-red-400 dark:border-red-600 p-4">
            <p className="text-red-700 dark:text-red-400">{error}</p>
            <button
              onClick={() => setError(null)}
              className="mt-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 underline no-border"
            >
              Dismiss
            </button>
          </div>
        )}

        {activeTab === 'search' && (
          <motion.div
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            className="space-y-8"
          >
            <KeywordInput
              onSearch={handleSearch}
              loading={loading}
              userCredits={userCredits}
              onPurchaseClick={handlePurchaseClick}
            />

            {loading && <ProgressBar progress={progress} variant={isCustomSearch ? 'custom' : 'broad'} />}

            {results.length > 0 && !loading && (
              <ResultsDisplay
                results={results}
                keyword={currentKeyword}
                user={user}
                searchId={currentSearch?.id}
                onStatusesUpdated={handleStatusesUpdated}
                onContentGenerated={handleContentGenerated}
                onPurchaseClick={handlePurchaseClick}
                setActiveTab={setActiveTab}
              />
            )}

            {searchHistory.length > 0 && !loading && results.length === 0 && (
              <SearchHistory
                searches={searchHistory}
                onLoadSearch={handleLoadSearch}
                user={user}
                onSearchDeleted={handleSearchDeleted}
              />
            )}
          </motion.div>
        )}

        {activeTab === 'analytics' && (
          <AnalyticsDashboard
            user={user}
            onSearch={handleSearch}
            onLoadSearch={handleLoadSearch}
            userCredits={userCredits}
            onPurchaseClick={handlePurchaseClick}
            setActiveTab={setActiveTab}
          />
        )}

        {activeTab === 'history' && (
          <motion.div
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            className="space-y-8"
          >
            <SearchHistory
              searches={searchHistory}
              onLoadSearch={handleLoadSearch}
              user={user}
              onSearchDeleted={handleSearchDeleted}
            />
          </motion.div>
        )}

        {activeTab === 'credits' && (
          <motion.div
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            className="space-y-8"
          >
            <AnimatePresence>
              {showPaymentSuccess && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex items-start justify-between p-4 rounded-lg bg-green-50 border border-green-200 text-green-800"
                >
                  <span>✅ Purchase successful! You can see your transaction history below. Thank you, we appreciate your business. Now you can fetch links! 🐕</span>
                  <button
                    onClick={() => setShowPaymentSuccess(false)}
                    className="ml-4 text-green-700 hover:text-green-900 underline no-border"
                  >
                    Dismiss
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
            {isLoadingCredits ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D59A4C]"></div>
              </div>
            ) : (
              <>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Credits Overview</h2>
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
                      <div className="flex items-center mb-4">
                        <div className="bg-[#D59A4C30] p-3 rounded-lg mr-3">
                          <SafeIcon icon={FiCreditCard} className="w-6 h-6 text-[#D59A4C]" />
                        </div>
                        <div>
                          <p className="text-gray-600 dark:text-gray-400 text-sm">Current Balance</p>
                          <p className="text-2xl font-bold text-gray-900 dark:text-white">{userCredits.toFixed(1) || 0}</p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        <span className="font-medium">1 credit</span> = 1 search (finds ~50 opportunities)
                        <br />
                        <span className="font-medium">0.1 credit</span> = 1 AI content generation
                      </p>
                      <button
                        onClick={handlePurchaseClick}
                        className="w-full flex items-center justify-center px-4 py-2 bg-[#D59A4C] text-white rounded-lg hover:bg-[#B58C54] transition-colors"
                      >
                        <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
                        Add Credits
                      </button>
                    </div>
                  </div>

                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Purchase Credits</h2>
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
                      <p className="text-gray-600 dark:text-gray-400 mb-4">
                        Need more credits? Get instant access to backlink opportunities.
                      </p>
                      <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400 mb-6">
                        <li>Find more backlink opportunities</li>
                        <li>Generate AI content for your outreach</li>
                        <li>Credits never expire</li>
                      </ul>
                      <a
                        onClick={handlePurchaseClick}
                        className="text-[#D59A4C] hover:text-[#B58C54] font-medium cursor-pointer"
                      >
                        Buy Credits
                      </a>
                    </div>
                  </div>
                </div>

                {creditHistory && creditHistory.length > 0 ? (
                  <CreditHistory
                    transactions={creditHistory}
                    currentPage={creditHistoryPage}
                    totalPages={creditHistoryTotalPages}
                    onPageChange={handleCreditHistoryPageChange}
                  />
                ) : (
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                    <h2 className="text-xl font-semibold mb-4 dark:text-white">Credit History</h2>
                    <p className="text-gray-600 dark:text-gray-400 text-center py-6">No credit transactions yet</p>
                  </div>
                )}
              </>
            )}
          </motion.div>
        )}
      </main>
    </div>
  )
}

export default Dashboard
